import { useState, useEffect } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Badge } from '@/components/ui/badge';
import { Calendar, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useTaskPopup } from '@/contexts/TaskPopupContext';

interface DueDateTask {
  id: string;
  title?: string;
  name?: string;
  status?: string;
  task_status?: string;
  type: 'foundational' | 'growth' | 'recurring';
  assigned_to?: string;
  owner?: string;
  due_date?: string;
  priority?: string;
  difficulty?: string;
  description?: string;
  images?: any;
  attachments?: any;
  brand_id?: string;
  sprint_id?: string;
  assignee_name?: string;
  assignee_alias?: string;
  [key: string]: any; // Allow all other fields
}

interface DueDateTasksHoverCardProps {
  dueDate: string | null;
  brandId: string;
  children: React.ReactNode;
}

export const DueDateTasksHoverCard = ({ dueDate, brandId, children }: DueDateTasksHoverCardProps) => {
  const [tasks, setTasks] = useState<DueDateTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { openTaskPopup } = useTaskPopup();

  useEffect(() => {
    if (dueDate && brandId) {
      fetchTasksForDate();
    }
  }, [dueDate, brandId]);

  const fetchTasksForDate = async () => {
    if (!dueDate) return;

    setIsLoading(true);
    try {
      // Fetch foundational tasks with all fields
      const { data: foundationalTasks } = await supabase
        .from('foundational_tasks')
        .select(`
          *,
          growth_squad!inner(name, alias)
        `)
        .eq('brand_id', brandId)
        .eq('due_date', dueDate);

      // Fetch growth tasks with all fields
      const { data: growthTasks } = await supabase
        .from('growth_tasks')
        .select('*')
        .eq('brand_id', brandId)
        .eq('due_date', dueDate);

      // Fetch recurring tasks with all fields
      const { data: recurringTasks } = await supabase
        .from('recurring_tasks')
        .select(`
          *,
          growth_squad!inner(name, alias)
        `)
        .eq('brand_id', brandId)
        .eq('due_date', dueDate);

      const allTasks: DueDateTask[] = [
        ...(foundationalTasks || []).map(task => ({
          ...task,
          type: 'foundational' as const,
          assignee_name: task.growth_squad?.name,
          assignee_alias: task.growth_squad?.alias,
        })),
        ...(growthTasks || []).map(task => ({
          ...task,
          type: 'growth' as const,
          assignee_name: undefined, // Growth tasks need separate lookup via assigned_to
          assignee_alias: undefined,
        })),
        ...(recurringTasks || []).map(task => ({
          ...task,
          type: 'recurring' as const,
          assignee_name: task.growth_squad?.name,
          assignee_alias: task.growth_squad?.alias,
        })),
      ];

      setTasks(allTasks);
    } catch (error) {
      console.error('Error fetching tasks for date:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'in_progress':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'done':
        return 'Done';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const handleTaskClick = (task: DueDateTask) => {
    openTaskPopup(task as any, task.type);
  };

  if (!dueDate) {
    return <>{children}</>;
  }

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-[400px] p-0 z-[9999]" 
        align="start"
        side="right"
      >
        <div className="p-4 border-b bg-muted/50">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Calendar className="h-4 w-4" />
            <span>Other Pending Tasks due on {format(new Date(dueDate), 'MMM dd, yyyy')}</span>
          </div>
        </div>
        
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              Loading tasks...
            </div>
          ) : tasks.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No other tasks due on this date
            </div>
          ) : (
            <div className="py-2">
              {tasks.map((task) => (
                <div 
                  key={task.id} 
                  className="px-4 py-2 hover:bg-muted/50 transition-colors flex items-center gap-3 cursor-pointer"
                  onClick={() => handleTaskClick(task)}
                >
                  <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                  <span className="text-sm flex-1 truncate" title={task.title || task.name}>
                    {task.title || task.name}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {task.assignee_name ? (
                      <>
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                          {task.assignee_alias?.slice(0, 2).toUpperCase() || 'U'}
                        </div>
                        <span className="text-xs text-muted-foreground max-w-[80px] truncate" title={task.assignee_name}>
                          {task.assignee_name}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Unassigned
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
